import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Spin, Typography } from 'antd';
import { EnvironmentOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  supabase,
  TripMapView,
  GpsTrackHistoryList,
  parseGeoPoint,
  formatDate,
  resolveTripMapPathDisplay,
  locationLogsToTrackPoints,
  buildTripMapMarkers,
  type LocationLog,
  type Trip,
  type LatLng,
  type TrackPoint,
  type TripMapViewHandle,
} from '@ubus/shared';
import PageHeader from '../components/PageHeader';
import StatusTag from '../components/StatusTag';
import { formatTripBrief } from '../utils/formatTripBrief';
import { resolveAdminBreadcrumbs } from '../utils/adminBreadcrumbs';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

type TripWithRelations = Trip & {
  drivers?: { name: string } | null;
  vehicles?: { fleet_number: string } | null;
  organizations?: { name: string } | null;
};

function TripMap() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbs = resolveAdminBreadcrumbs(location.pathname, t);
  void i18n.language;
  const mapRef = useRef<TripMapViewHandle>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const [loading, setLoading] = useState(true);
  const [trip, setTrip] = useState<TripWithRelations | null>(null);
  const [logs, setLogs] = useState<LocationLog[]>([]);
  const [vehiclePosition, setVehiclePosition] = useState<LatLng | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [routePlanningError, setRoutePlanningError] = useState<string | null>(null);

  const pickup = trip?.pickup_geopoint ? parseGeoPoint(trip.pickup_geopoint) : null;
  const dropoff = trip?.dropoff_geopoint ? parseGeoPoint(trip.dropoff_geopoint) : null;
  const center = vehiclePosition ?? pickup ?? dropoff ?? { lat: -33.8688, lng: 151.2093 };

  const trackPoints = useMemo(() => locationLogsToTrackPoints(logs), [logs]);

  const pathDisplay = useMemo(
    () => resolveTripMapPathDisplay(trackPoints, pickup, dropoff),
    [trackPoints, pickup, dropoff]
  );

  const markers = useMemo(
    () =>
      buildTripMapMarkers({
        pickup,
        dropoff,
        vehicle: vehiclePosition,
        trackPoints,
        showTrackPoints: pathDisplay.mode === 'track',
      }),
    [pickup, dropoff, vehiclePosition, trackPoints, pathDisplay.mode]
  );

  const missingCoordinates = !pickup && !dropoff;

  const fetchTrip = useCallback(async () => {
    if (!id) return null;
    const { data, error } = await supabase
      .from('trips')
      .select('*, vehicles(*), drivers(*), organizations(*)')
      .eq('id', id)
      .single();
    if (error || !data) return null;
    setTrip(data as TripWithRelations);
    return data;
  }, [id]);

  const loadLocationLogs = useCallback(async () => {
    if (!id) return;
    const { data } = await supabase
      .from('location_logs')
      .select('*')
      .eq('trip_id', id)
      .order('timestamp', { ascending: true });

    const rows = (data ?? []) as LocationLog[];
    setLogs(rows);

    const latest = rows[rows.length - 1];
    if (latest?.geopoint) {
      const parsed = parseGeoPoint(latest.geopoint);
      if (parsed) {
        setVehiclePosition(parsed);
        setLastUpdate(latest.timestamp);
      }
    }
  }, [id]);

  const subscribeToLocationUpdates = useCallback(() => {
    if (!id) return;
    if (channelRef.current) supabase.removeChannel(channelRef.current);

    channelRef.current = supabase
      .channel(`admin_trip_map_${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'location_logs',
          filter: `trip_id=eq.${id}`,
        },
        (payload) => {
          const newLog = payload.new as LocationLog;
          setLogs((prev) => [...prev, newLog]);
          const parsed = newLog.geopoint ? parseGeoPoint(newLog.geopoint) : null;
          if (parsed) {
            setVehiclePosition(parsed);
            setLastUpdate(newLog.timestamp);
            mapRef.current?.updateMarker('vehicle', parsed);
            mapRef.current?.panTo(parsed);
          }
        }
      )
      .subscribe();
  }, [id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchTrip();
      await loadLocationLogs();
      setLoading(false);
    })();
  }, [fetchTrip, loadLocationLogs]);

  useEffect(() => {
    subscribeToLocationUpdates();
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [subscribeToLocationUpdates]);

  const handleMapReady = async () => {
    await loadLocationLogs();
  };

  if (loading) {
    return (
      <div className="admin-section-page__loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="admin-list-page admin-page--fluid-bleed admin-page--density-ops">
        <PageHeader
          title={t('tripMap.title')}
          subtitle={t('tripMap.notFound')}
          breadcrumbs={breadcrumbs}
          extra={
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/trips')}>
              {t('tripMap.back')}
            </Button>
          }
        />
        <Card className="admin-table-card" bordered={false}>
          <div style={{ padding: 24 }}>
            <Text type="danger">{t('tripMap.accessError')}</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="admin-list-page admin-page--fluid-bleed admin-page--density-ops">
      <PageHeader
        title={`${t('tripMap.title')} — ${formatTripBrief(trip)}`}
        subtitle={t('tripMap.liveRoute', { date: formatDate(trip.date) })}
        breadcrumbs={breadcrumbs}
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/trips')}>
            {t('tripMap.back')}
          </Button>
        }
      />

      <Card className="admin-table-card" bordered={false}>
        <div className="admin-trip-map-meta">
          <StatusTag status={trip.status} />
          <Text>
            <strong>{t('tripMap.driver')}</strong> {trip.drivers?.name ?? '—'}
          </Text>
          <Text>
            <strong>{t('tripMap.vehicle')}</strong> {trip.vehicles?.fleet_number ?? '—'}
          </Text>
          <Text type="secondary">{t('tripMap.gpsPoints', { count: logs.length })}</Text>
          <Text type="secondary">
            {t('tripMap.map')}{' '}
            {pathDisplay.mode === 'track'
              ? t('tripMap.gpsTrack', { count: trackPoints.length })
              : pathDisplay.mode === 'planned'
                ? t('tripMap.plannedRoute')
                : '—'}
          </Text>
          {lastUpdate ? (
            <Text type="secondary">{t('tripMap.lastUpdate', { time: new Date(lastUpdate).toLocaleString() })}</Text>
          ) : null}
        </div>

        {mapError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('tripMap.mapFailed')}
            description={mapError}
          />
        ) : null}

        {routePlanningError && pathDisplay.mode === 'planned' ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('tripMap.routeFailed')}
            description={routePlanningError}
          />
        ) : null}

        {missingCoordinates ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('tripMap.noCoordinates')}
            description={t('tripMap.noCoordinatesHelp')}
            action={
              <Button size="small" onClick={() => navigate('/trips')}>
                {t('tripMap.edit')}
              </Button>
            }
          />
        ) : null}

        <div className="admin-trip-map-viewport">
          <TripMapView
            ref={mapRef}
            center={center}
            zoom={13}
            markers={markers}
            track={pathDisplay.track}
            route={pathDisplay.route}
            onReady={handleMapReady}
            onError={(message) => setMapError(message)}
            onRoutePlanningError={(message) => setRoutePlanningError(message)}
            onRoutePlanningSuccess={() => setRoutePlanningError(null)}
          />
        </div>

        {pathDisplay.mode === 'track' && trackPoints.length > 0 ? (
          <GpsTrackHistoryList
            points={trackPoints}
            onSelectPoint={(point: TrackPoint) => mapRef.current?.panTo(point)}
          />
        ) : null}

        <div className="admin-trip-map-addresses">
          <div className="admin-trip-map-address">
            <EnvironmentOutlined className="admin-trip-map-address__icon--pickup" aria-hidden />
            <div>
              <Text strong>{t('tripMap.pickup')} </Text>
              <Text>{trip.pickup_address}</Text>
            </div>
          </div>
          <div className="admin-trip-map-address">
            <EnvironmentOutlined className="admin-trip-map-address__icon--dropoff" aria-hidden />
            <div>
              <Text strong>{t('tripMap.dropoff')} </Text>
              <Text>{trip.dropoff_address}</Text>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default TripMap;
